import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Mail, X } from "lucide-react";
import { usePager, Pager } from "@/components/ui/TablePager";

export default function StudentsPage() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/students");
      setStudents(data.students || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Remove student from your roster?")) return;
    try { await api.delete(`/students/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const sendReminder = async (id) => {
    try { await api.post(`/students/${id}/fee-reminder`); toast.success("Reminder sent"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const filtered = students.filter((s) => !q || (s.full_name + " " + s.email + " " + (s.grade || "") + " " + (s.parent_email || "")).toLowerCase().includes(q.toLowerCase()));
  const { page, setPage, pages, pageItems, total } = usePager(filtered, 25);
  const toggle = (id) => setSelected((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]));
  const allChecked = filtered.length > 0 && filtered.every((s) => selected.includes(s.id));
  const toggleAll = () => setSelected(allChecked ? [] : filtered.map((s) => s.id));

  const bulkDelete = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Remove ${selected.length} student${selected.length > 1 ? "s" : ""} from your roster?`)) return;
    const res = await Promise.allSettled(selected.map((id) => api.delete(`/students/${id}`)));
    const ok = res.filter((r) => r.status === "fulfilled").length;
    toast.success(`Removed ${ok} student${ok === 1 ? "" : "s"}`);
    setSelected([]);
    load();
  };

  return (
    <div className="space-y-6" data-testid="students-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">/ students</div>
          <h1 className="font-display font-bold text-3xl tracking-tighter">Your students</h1>
        </div>
        <button data-testid="add-student-btn" onClick={() => setShowAdd(true)} className="inline-flex items-center gap-2 bg-[#FFD600] text-[#0A0C10] font-bold px-5 py-2.5 btn-brutal-shadow">
          <Plus size={16} /> Add student
        </button>
      </div>

      <div className="bg-white border border-neutral-200 overflow-x-auto">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-neutral-200 flex-wrap">
          <input
            value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search students…"
            data-testid="students-search"
            className="flex-1 min-w-[180px] max-w-xs text-sm border border-neutral-300 rounded-md px-3 py-2 outline-none focus:border-[#002FA7]"
          />
          {selected.length > 0 && (
            <button onClick={bulkDelete} data-testid="students-bulk-delete"
              className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-md bg-red-500/10 text-red-500 border border-red-400/40 hover:bg-red-500/20">
              <Trash2 size={12} /> Remove selected ({selected.length})
            </button>
          )}
        </div>
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">
            <tr>
              <th className="px-4 py-3 w-8"><input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="students-select-all" /></th>
              <th className="px-5 py-3">Student</th>
              <th className="px-5 py-3">Grade</th>
              <th className="px-5 py-3">Fee</th>
              <th className="px-5 py-3">Parent</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-5 py-8 text-neutral-500">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-12 text-center text-neutral-500">
                {students.length === 0 ? 'No students yet. Click "Add student" to invite your first one.' : "No students match your search."}
              </td></tr>
            ) : pageItems.map((s) => (
              <tr key={s.id} className="border-t border-neutral-100">
                <td className="px-4 py-3"><input type="checkbox" checked={selected.includes(s.id)} onChange={() => toggle(s.id)} data-testid={`student-check-${s.id}`} /></td>
                <td className="px-5 py-3">
                  <div className="font-medium">{s.full_name}</div>
                  <div className="text-xs text-neutral-500">{s.email}</div>
                </td>
                <td className="px-5 py-3 text-neutral-600">{s.grade || "—"}</td>
                <td className="px-5 py-3 text-neutral-600">${(s.fee_amount || 0).toFixed(2)} <span className="text-xs">/ {s.billing_cycle}</span></td>
                <td className="px-5 py-3 text-neutral-600">{s.parent_email || "—"}</td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => sendReminder(s.id)} className="text-xs px-2.5 py-1.5 border border-neutral-200 hover:border-[#0A0C10] mr-1" data-testid={`fee-reminder-${s.id}`}>
                    <Mail size={12} className="inline" /> Reminder
                  </button>
                  <button onClick={() => remove(s.id)} className="text-xs px-2.5 py-1.5 border border-neutral-200 hover:border-red-500 text-red-500" data-testid={`remove-student-${s.id}`}>
                    <Trash2 size={12} className="inline" /> Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pager page={page} pages={pages} total={total} onPage={setPage} testid="students-pager" />
      </div>

      {showAdd && <AddStudentModal onClose={() => setShowAdd(false)} onAdded={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

function AddStudentModal({ onClose, onAdded }) {
  const [form, setForm] = useState({ full_name: "", email: "", parent_email: "", phone: "", grade: "", subject: "", fee_amount: 0, billing_cycle: "monthly", notes: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/students", { ...form, fee_amount: Number(form.fee_amount) || 0 });
      toast.success(res.data.invite_password ? `Invite sent. Temp password: ${res.data.invite_password}` : "Student added");
      onAdded();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500">/ add student</div>
            <h2 className="font-display font-bold text-2xl tracking-tight">New student</h2>
          </div>
          <button onClick={onClose} aria-label="Close"><X /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input className={inp} placeholder="Full name *" required value={form.full_name} onChange={set("full_name")} data-testid="add-student-name" />
          <input className={inp} type="email" placeholder="Student email (optional)" value={form.email} onChange={set("email")} data-testid="add-student-email" />
          <input className={inp} type="email" placeholder="Parent email" value={form.parent_email} onChange={set("parent_email")} />
          <div className="grid grid-cols-2 gap-3">
            <input className={inp} placeholder="Grade" value={form.grade} onChange={set("grade")} />
            <input className={inp} placeholder="Subject" value={form.subject} onChange={set("subject")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input className={inp} placeholder="Phone" value={form.phone} onChange={set("phone")} />
            <input className={inp} type="number" placeholder="Fee $" value={form.fee_amount} onChange={set("fee_amount")} />
          </div>
          <select className={inp} value={form.billing_cycle} onChange={set("billing_cycle")}>
            <option value="monthly">Monthly</option>
            <option value="weekly">Weekly</option>
            <option value="quarterly">Quarterly</option>
            <option value="one-time">One-time</option>
          </select>
          <textarea className={inp} rows={2} placeholder="Notes" value={form.notes} onChange={set("notes")} />
          <button data-testid="add-student-submit" type="submit" disabled={loading} className="w-full bg-[#0A0C10] text-white font-bold py-3 disabled:opacity-60">{loading ? "Adding..." : "Add & send invite"}</button>
        </form>
      </div>
    </div>
  );
}

const inp = "w-full bg-white border border-neutral-300 px-4 py-2.5 focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none";
